<script setup>
import {onMounted, ref, watch, computed} from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {getAutosuggest, getReverseGeocode, getRoute} from "@/services/hereApi";
import RequiredComponent from "@/components/utils/RequiredComponent.vue";
import {formatISODate} from "@/globals/utils/formatDate";
import {createRide} from "@/services/tripService";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import {customToast} from "@/globals/utils/customToast";
import BackButtonComponent from "@/components/utils/BackButtonComponent.vue";
import {useUserStore} from "@/stores/user";
import DefaultButton from '@/components/utils/DefaultButton.vue';

// --- ESTADOS / REFS
const pickupAddress = ref('Southwest Florida Int\'l Airport (RSW)');
const pickupLatitude = ref(26.53375458982706);
const pickupLongitude = ref(-81.7595054497816);

const destinationAddress = ref('');
const destinationLatitude = ref(0.0);
const destinationLongitude = ref(0.0);

const price = ref(0.0);
const paymentMethod = ref('cash');
const distance = ref(0.0);
const duration = ref(0.0);
const durationForHumans = ref('');
const clientName = ref('');
const clientPhone = ref('');
const clientId = ref('');
const selectedDriver = ref({});
const bookingDate = ref('');
const polyline = ref('');

// Estado cargando
const loading = ref(false);

// userStore
const userStore = useUserStore();

// Reglas de validación
const validateRequiredInputs = {
  pickupAddress: {required: true, message: 'Pickup address is required'},
  pickupLatitude: {required: true, message: 'Pickup latitude is required'},
  pickupLongitude: {required: true, message: 'Pickup longitude is required'},
  destinationAddress: {required: true, message: 'Destination address is required'},
  destinationLatitude: {required: true, message: 'Destination latitude is required'},
  destinationLongitude: {required: true, message: 'Destination longitude is required'},
  price: {required: true, type: 'number', message: 'Price is required'},
  paymentMethod: {required: true, message: 'Payment method is required'},
  clientName: {required: true, message: 'Client name is required'},
  clientPhone: {required: true, message: 'Client phone is required'},
  bookingDate: {required: false, message: ''},
};

// Estado de errores por campo
const fieldErrors = ref({});
// Estado de campos tocados
const touchedFields = ref({});

// Función para obtener el valor de un campo por su nombre
const getFieldValue = (fieldName) => {
  switch (fieldName) {
    case 'pickupAddress':
      return pickupAddress.value;
    case 'pickupLatitude':
      return pickupLatitude.value;
    case 'pickupLongitude':
      return pickupLongitude.value;
    case 'destinationAddress':
      return destinationAddress.value;
    case 'destinationLatitude':
      return destinationLatitude.value;
    case 'destinationLongitude':
      return destinationLongitude.value;
    case 'price':
      return price.value;
    case 'paymentMethod':
      return paymentMethod.value;
    case 'clientName':
      return clientName.value;
    case 'clientPhone':
      return clientPhone.value;
    default:
      return null;
  }
};

// Validar un campo específico
const validateField = (fieldName) => {
  const rule = validateRequiredInputs[fieldName];
  const value = getFieldValue(fieldName);

  let error = '';
  if (rule.required && (!value || (typeof value === 'number' && value === 0))) {
    error = rule.message;
  } else if (rule.type === 'number' && isNaN(Number(value))) {
    error = `${fieldName} must be a number`;
  }

  fieldErrors.value[fieldName] = error;
};

// Cada vez que se modifique un campo, si ya fue tocado, validarlo en tiempo real
const handleInput = (fieldName) => {
  if (touchedFields.value[fieldName]) {
    validateField(fieldName);
  }
};

// Comprobamos si hay errores en algún campo
const isCreateTripDisabled = computed(() => {
  return Object.keys(validateRequiredInputs).some((fieldName) => {
    validateField(fieldName);
    return fieldErrors.value[fieldName];
  });
});

// Mapa
let map = null;
const mapContainer = ref(null);
const showMap = ref(false);
const mapLoading = ref(false);
let currentMarker = null;
const selectedIsPickup = ref(true);
const selectedLocation = ref({lat: 0, lng: 0, address: ''});

// Sugerencias direcciones
const suggestions = ref([]);
const searchingSuggestions = ref(false);
let debounceTimeout = null;

const activeInputType = ref("");
const suggestionsVisible = ref(false);

const searchSuggestions = (type, query) => {
  if (query.length < 3) {
    suggestions.value = [];
    return;
  }

  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(async () => {
    searchingSuggestions.value = true;
    try {
      suggestions.value = await getAutosuggest(query);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      searchingSuggestions.value = false;
    }
  }, 300);
};

const showSuggestions = (type) => {
  activeInputType.value = type;
  suggestionsVisible.value = true;
};

const hideSuggestions = () => {
  suggestionsVisible.value = false;
  suggestions.value = [];
  activeInputType.value = "";
};

const selectSuggestion = (suggestion) => {
  if (activeInputType.value === "pickup") {
    pickupAddress.value = suggestion.title;
    pickupLatitude.value = suggestion.position.lat;
    pickupLongitude.value = suggestion.position.lng;
    validateField('pickupAddress');
    validateField('pickupLatitude');
    validateField('pickupLongitude');
  } else {
    destinationAddress.value = suggestion.title;
    destinationLatitude.value = suggestion.position.lat;
    destinationLongitude.value = suggestion.position.lng;
    validateField('destinationAddress');
    validateField('destinationLatitude');
    validateField('destinationLongitude');
  }
  hideSuggestions();
};

const pickOnMap = (type, isPickup = true) => {
  selectedIsPickup.value = isPickup;
  showMap.value = true;
  initMap();
};

const initMap = () => {
  setTimeout(() => {
    if (map) {
      map.remove();
    }

    let initialLat, initialLng;
    if (selectedIsPickup.value) {
      initialLat = pickupLatitude.value || 26.517938;
      initialLng = pickupLongitude.value || -81.760655;
    } else {
      initialLat = destinationLatitude.value || 26.517938;
      initialLng = destinationLongitude.value || -81.760655;
    }

    map = L.map(mapContainer.value).setView([initialLat, initialLng], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    if ((selectedIsPickup.value && pickupLatitude.value && pickupLongitude.value) ||
        (!selectedIsPickup.value && destinationLatitude.value && destinationLongitude.value)) {
      currentMarker = L.marker([initialLat, initialLng]).addTo(map);
      if (selectedIsPickup.value && pickupAddress.value) {
        currentMarker.bindPopup(`${pickupAddress.value}`).openPopup();
      } else if (!selectedIsPickup.value && destinationAddress.value) {
        currentMarker.bindPopup(`${destinationAddress.value}`).openPopup();
      }
    }

    map.on('click', async (e) => {
      const {lat, lng} = e.latlng;
      if (currentMarker) {
        map.removeLayer(currentMarker);
      }
      currentMarker = L.marker([lat, lng]).addTo(map);

      try {
        const data = await getReverseGeocode(lat, lng);
        selectedLocation.value = {
          lat: lat,
          lng: lng,
          address: data.items[0].address.label
        };
      } catch (error) {
        console.error('Error getting reverse geocode:', error);
      }
    });
  }, 1000);
};

const setAddress = () => {
  if (selectedIsPickup.value) {
    pickupAddress.value = selectedLocation.value.address;
    pickupLatitude.value = selectedLocation.value.lat;
    pickupLongitude.value = selectedLocation.value.lng;
    validateField('pickupAddress');
    validateField('pickupLatitude');
    validateField('pickupLongitude');
  } else {
    destinationAddress.value = selectedLocation.value.address;
    destinationLatitude.value = selectedLocation.value.lat;
    destinationLongitude.value = selectedLocation.value.lng;
    validateField('destinationAddress');
    validateField('destinationLatitude');
    validateField('destinationLongitude');
  }

  selectedLocation.value = {lat: 0, lng: 0, address: ''};
  showMap.value = false;
};

const createTripAction = async () => {
  // Validar todos los campos antes de crear el viaje
  Object.keys(validateRequiredInputs).forEach((fieldName) => {
    validateField(fieldName);
  });

  if (isCreateTripDisabled.value) {
    return;
  }

  loading.value = true;

  const tripData = {
    pickup: {
      address: pickupAddress.value,
      location: {latitude: pickupLatitude.value, longitude: pickupLongitude.value},
    },
    destination: {
      address: destinationAddress.value,
      location: {latitude: destinationLatitude.value, longitude: destinationLongitude.value},
    },
    price: price.value,
    paymentMethod: paymentMethod.value,
    distance: distance.value,
    duration: duration.value,
    user: {
      name: clientName.value,
      phone: clientPhone.value,
      id: clientId.value,
    },
    driver: selectedDriver.value._id,
    bookingDate: bookingDate.value ? formatISODate(bookingDate.value) : null,
    nearby: null,
    polyline: polyline.value,
  };

  try {
    const trip = await createRide(tripData);
    console.log('Trip created:', trip);
    loading.value = false;
    customToast('Trip created successfully!', 'success');
    // Limpiar campos
    pickupAddress.value = '';
    pickupLatitude.value = 0.0;
    pickupLongitude.value = 0.0;
    destinationAddress.value = '';
    destinationLatitude.value = 0.0;
    destinationLongitude.value = 0.0;
    price.value = 0.0;
    paymentMethod.value = 'cash';
    distance.value = 0.0;
    duration.value = 0.0;
    selectedDriver.value = {};
    bookingDate.value = '';
    durationForHumans.value = '';
  } catch (error) {
    console.error('Error creating trip:', error);
    customToast('Error creating trip', 'error');
    loading.value = false;
  }
};

// Calcular ruta al cambiar las coordenadas
watch([pickupLatitude, pickupLongitude, destinationLatitude, destinationLongitude], async () => {
  if (pickupLatitude.value && pickupLongitude.value && destinationLatitude.value && destinationLongitude.value) {
    let originLonLat = {latitude: pickupLatitude.value, longitude: pickupLongitude.value};
    let destinationLonLat = {latitude: destinationLatitude.value, longitude: destinationLongitude.value};

    try {
      const data = await getRoute(originLonLat, destinationLonLat);
      const json = data.routes[0].sections[0];
      let basePrice = 5;
      let costPerKm = 1.5;
      let distanceInKm = json.summary.length / 1000;

      price.value = (distanceInKm < 1 ? basePrice : basePrice + (distanceInKm - 1) * costPerKm).toFixed(2);
      distance.value = (json.summary.length * 0.000621371).toFixed(2);
      duration.value = json.summary.duration;
      polyline.value = json.polyline;
      durationForHumans.value = `${Math.floor(json.summary.duration / 3600)}h ${Math.floor((json.summary.duration % 3600) / 60)}m`;
      validateField('price');
    } catch (error) {
      console.error('Error getting route:', error);
    }
  }
});

onMounted(async () => {
  // Inicializar campos tocados en false y errores vacíos
  for (const key in validateRequiredInputs) {
    touchedFields.value[key] = false;
    fieldErrors.value[key] = '';
  }
  // Validar inicialmente
  Object.keys(validateRequiredInputs).forEach((fieldName) => {
    validateField(fieldName);
  });

  // Asignar usuario
  let user = await userStore.info();
  clientName.value = user.name;
  clientPhone.value = user.phone;
  clientId.value = user._id;

});
</script>

<template>

  <BackButtonComponent url="/user/trips" />

  <LoadingComponent v-if="loading" />

  <!-- Overlay del mapa -->
  <div id="pickOnMap" :class="{ 'hideMap': !showMap }">
    <div class="h-full w-full flex flex-col items-center justify-center relative">
      <div v-if="mapLoading" class="w-full h-full flex items-center justify-center fixed top-0 left-0 z-50">
        <div class="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>

      <div ref="mapContainer" class="w-full h-full rounded shadow-lg"></div>

      <div class="p-3 bg-white dark:bg-gray-800 w-full flex flex-col justify-center items-center" v-if="!mapLoading">
        <div class="flex space-x-4 items-center">
          <button
              class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 p-2 rounded font-bold text-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              @click="showMap = false"
          >
            Cancel
          </button>
          <button
              class="bg-blue-600 text-white p-2 rounded font-bold text-lg hover:bg-blue-700 transition"
              @click="setAddress"
          >
            Confirm
          </button>
          <strong v-if="selectedLocation.address" class="text-gray-700 dark:text-gray-300">{{ selectedLocation.address }}</strong>
        </div>
      </div>
    </div>
  </div>

  <!-- Contenido Principal -->
  <div class="flex justify-center items-center">
    <div class="max-w-7xl w-full bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg relative">

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Pickup Address -->
        <div class="relative">
          <label for="pickup" class="block text-gray-700 dark:text-gray-300 font-medium">
            Pickup Address
            <RequiredComponent name="Pickup address"/>
          </label>
          <input
              id="pickup"
              type="text"
              v-model="pickupAddress"
              :class="[touchedFields.pickupAddress && fieldErrors.pickupAddress ? 'border-red-500' : '', 'w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4']"
              @focus="showSuggestions('pickup')"
              @blur="touchedFields.pickupAddress = true; validateField('pickupAddress'); hideSuggestions()"
              @input="handleInput('pickupAddress'); searchSuggestions('pickup', $event.target.value)"
              autocomplete="off"
          />
          <div v-if="touchedFields.pickupAddress && fieldErrors.pickupAddress" class="text-red-500 text-sm mb-2">
            {{ fieldErrors.pickupAddress }}
          </div>
          <div v-if="suggestionsVisible && activeInputType === 'pickup'"
               class="absolute bg-white dark:bg-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-600 shadow-lg rounded z-50"
               style="top: 55%; left: 0; width: 100%;">
            <ul class="p-2 max-h-48 overflow-auto">
              <li
                  v-for="suggestion in suggestions.items"
                  :key="suggestion.id"
                  @mousedown="selectSuggestion(suggestion)"
                  class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                {{ suggestion.title }} {{ suggestion.title }} <br> <small v-if="suggestion.address.label" class="text-gray-600 dark:text-gray-200">{{ suggestion.address.label }}</small>
              </li>
            </ul>
          </div>
          <DefaultButton title="Pick on Map" @click="pickOnMap('pickup', true)" />
        </div>

        <!-- Destination Address -->
        <div class="relative">
          <label for="destination" class="block text-gray-700 dark:text-gray-300 font-medium">
            Destination Address
            <RequiredComponent name="Destination address"/>
          </label>
          <input
              id="destination"
              type="text"
              v-model="destinationAddress"
              :class="[touchedFields.destinationAddress && fieldErrors.destinationAddress ? 'border-red-500' : '', 'w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4']"
              @focus="showSuggestions('destination')"
              @blur="touchedFields.destinationAddress = true; validateField('destinationAddress'); hideSuggestions()"
              @input="handleInput('destinationAddress'); searchSuggestions('destination', $event.target.value)"
              autocomplete="off"
          />
          <div v-if="touchedFields.destinationAddress && fieldErrors.destinationAddress"
               class="text-red-500 text-sm mb-2">
            {{ fieldErrors.destinationAddress }}
          </div>
          <div v-if="suggestionsVisible && activeInputType === 'destination'"
               class="absolute bg-white dark:bg-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-600 shadow-lg rounded z-50"
               style="top: 55%; left: 0; width: 100%;">
            <ul class="p-2 max-h-48 overflow-auto">
              <li
                  v-for="suggestion in suggestions.items"
                  :key="suggestion.id"
                  @mousedown="selectSuggestion(suggestion)"
                  class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                {{ suggestion.title }}{{ suggestion.title }} <br> <small v-if="suggestion.address.label" class="text-gray-600 dark:text-gray-200">{{ suggestion.address.label }}</small>
              </li>
            </ul>
          </div>
          <DefaultButton title="Pick on Map" @click="pickOnMap('pickup', false)" />
        </div>

        <!-- Price -->
        <div>
          <label for="price" class="block text-gray-700 dark:text-gray-300 font-medium">Estimated Price
            <RequiredComponent name="Price"/>
          </label>
          <input
              id="price"
              type="number"
              readonly
              v-model="price"
              :class="[touchedFields.price && fieldErrors.price ? 'border-red-500' : '', 'w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4']"
              @blur="touchedFields.price = true; validateField('price')"
              @input="handleInput('price')"
          />
          <div v-if="touchedFields.price && fieldErrors.price" class="text-red-500 text-sm mb-2">
            {{ fieldErrors.price }}
          </div>
        </div>

        <!-- Payment Method -->
        <div>
          <label for="paymentMethod" class="block text-gray-700 dark:text-gray-300 font-medium">Payment Method</label>
          <select
              id="paymentMethod"
              v-model="paymentMethod"
              :class="[touchedFields.paymentMethod && fieldErrors.paymentMethod ? 'border-red-500' : '', 'w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4']"
              @blur="touchedFields.paymentMethod = true; validateField('paymentMethod')"
              @change="handleInput('paymentMethod')"
          >
            <option value="cash">Cash</option>
            <option value="square">Square</option>
          </select>
          <div v-if="touchedFields.paymentMethod && fieldErrors.paymentMethod" class="text-red-500 text-sm mb-2">
            {{ fieldErrors.paymentMethod }}
          </div>
        </div>

        <!-- Distance -->
        <div>
          <label for="distance" class="block text-gray-700 dark:text-gray-300 font-medium">Distance (Miles)</label>
          <input
              id="distance"
              type="number"
              v-model="distance"
              class="w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4"
              readonly
          />
        </div>

        <!-- Duration -->
        <div>
          <label for="duration" class="block text-gray-700 dark:text-gray-300 font-medium">Duration (Hrs/Mins)</label>
          <input
              id="duration"
              type="text"
              v-model="durationForHumans"
              class="w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4"
              readonly
          />
        </div>

        <!-- Booking Date -->
        <div>
          <label for="bookingDate" class="block text-gray-700 dark:text-gray-300 font-medium">Booking Date</label>
          <input
              id="bookingDate"
              type="datetime-local"
              v-model="bookingDate"
              class="w-full bg-gray-100 dark:bg-gray-700 text-black dark:text-gray-200 border border-gray-300 dark:border-gray-600 p-3 rounded mb-4"
              @blur="touchedFields.bookingDate = true; validateField('bookingDate')"
              @change="handleInput('bookingDate')"
          />
        </div>
      </div>

      <!-- Confirm Button -->
      <div class="text-center mt-8">
        <DefaultButton 
          @click="createTripAction" 
          :title="bookingDate ? 'Booking Trip' : 'Confirm Trip'" 
          :disabled="isCreateTripDisabled" />
      </div>
    </div>
  </div>
</template>

<style scoped>
button:active {
  transform: scale(0.98);
}

#pickOnMap {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  overflow: hidden;
}

.hideMap {
  width: 0 !important;
  height: 0 !important;
}

.border-red-500 {
  border-color: #f56565 !important;
}
</style>

